import type { JSX, ReactNode } from 'react';
import { Logo } from '@viniciomendesr/rayrovisk-design-system';

/**
 * The frame the three password screens share: centred card on the cream ground,
 * wordmark above it.
 *
 * It lives in its own file rather than inside one of the screens so none of the
 * three has to import another, and so the entry surfaces keep one shape instead
 * of three that drift apart a commit at a time. Every value here is a design
 * system token — nothing in this product carries a copied hex (ADR-0009).
 */
export function AuthLayout({ children }: { children: ReactNode }): JSX.Element {
  return (
    <section
      style={{
        minHeight: '100vh',
        background: 'var(--rvk-color-cream-50)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '56px 24px'
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 412,
          display: 'flex',
          flexDirection: 'column',
          gap: 22
        }}
      >
        <BrandHeader />
        {children}
      </div>
    </section>
  );
}

/**
 * Exported because the magic-link landing uses it too. Every screen a person
 * can reach before the app shell exists has to say whose product she is looking
 * at: the callback in particular is where she arrives from her inbox, which is
 * the worst place to land on an unbranded page.
 */
export function BrandHeader(): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      <span
        style={{
          font: '400 32px/1.05 var(--rvk-font-expressive)',
          letterSpacing: '-.02em',
          color: 'var(--rvk-text-primary)'
        }}
      >
        Roteirovisk
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span
          style={{
            font: '600 9.5px/1 var(--rvk-font-functional)',
            letterSpacing: '.16em',
            textTransform: 'uppercase',
            color: 'var(--rvk-text-tertiary)'
          }}
        >
          um produto
        </span>
        {/*
         * An attribution, so it reads below the product's own name rather than
         * over it: at 120 the lettering rendered 64px tall next to a 34px title
         * and a 10px label, which is the wrong thing being loud. This puts the
         * script at roughly 24px — still well clear of the ~14px floor where
         * the design system says the lettering stops holding up.
         */}
        <Logo size={34} />
      </div>
    </div>
  );
}

/** Body copy at the size the entry screens use. */
export function AuthText({ children }: { children: ReactNode }): JSX.Element {
  return (
    <p
      style={{
        margin: 0,
        font: '400 13.5px/1.55 var(--rvk-font-functional)',
        color: 'var(--rvk-text-secondary)'
      }}
    >
      {children}
    </p>
  );
}
